import { setup } from 'xstate5';

import {
  AuthMachineActions,
  AuthMachineActors,
  AuthMachineDelays,
  AuthMachineGuards,
} from './setup';

import type { AuthContext, AuthEvent } from './setup';

// ─────────────────────────────────────────────────────────────────────────────

const model = setup({
  types: {
    context: {} as AuthContext,
    events: {} as AuthEvent,
  },
  actions: AuthMachineActions,
  actors: AuthMachineActors,
  delays: AuthMachineDelays,
  guards: AuthMachineGuards,
});

// ─── Machine ─────────────────────────────────────────────────────────────────

export const authMachine = model.createMachine({
  id: 'auth-machine',
  context: (): AuthContext => ({
    currentCustomer: null,
    hasLoggedOut: false,
    isFirstAuthAttempt: true,
    postLoginRedirectHref: undefined,
    sessionError: undefined,
    sessionFailureCount: 0,
    sessionRetryAt: undefined,
  }),
  invoke: {
    id: 'authBroadcast',
    src: 'authBroadcast',
  },
  initial: 'fetching-session',
  states: {
    'fetching-session': {
      entry: 'clearSessionError',
      invoke: {
        id: 'fetchSession',
        src: 'fetchSession',
        onDone: [
          {
            target: 'fetching.customer',
            guard: {
              type: 'checkIfSessionIsLoggedIn',
              params: ({ event }) => ({ isLoggedIn: event.output.isLoggedIn }),
            },
            actions: {
              type: 'persistCsrfToken',
              params: ({ event }) => ({ csrfToken: event.output.csrfToken }),
            },
          },
          {
            target: 'session.loggedOut',
            actions: {
              type: 'persistCsrfToken',
              params: ({ event }) => ({ csrfToken: event.output.csrfToken }),
            },
          },
        ],
        onError: {
          target: 'retrying-session',
          actions: [
            {
              type: 'storeSessionFailure',
              params: ({ event }) => ({ error: event.error }),
            },
            {
              type: 'logSessionFailure',
              params: ({ event }) => ({ error: event.error }),
            },
          ],
        },
      },
    },
    'retrying-session': {
      initial: 'checking-delay',
      states: {
        'checking-delay': {
          always: [
            { target: 'cooling-down', guard: 'checkIfSessionRetryIsDelayed' },
            { target: 'ready' },
          ],
        },
        'cooling-down': {
          entry: 'storeSessionRetryAt',
          exit: 'clearSessionRetryAt',
          after: {
            SESSION_RETRY_DELAY: 'ready',
          },
        },
        ready: {
          on: {
            RETRY_SESSION: '#auth-machine.fetching-session',
          },
        },
      },
    },
    fetching: {
      initial: 'customer',
      states: {
        customer: {
          invoke: {
            id: 'fetchCustomer',
            src: 'fetchCustomer',
            onDone: {
              target: '#auth-machine.session.loggedIn',
              actions: {
                type: 'storeCurrentCustomerData',
                params: ({ event }) => ({ currentCustomer: event.output }),
              },
            },
            onError: {
              target: '#auth-machine.session.loggedOut.customer-fetch-failed',
              actions: 'storeCustomerFetchFailure',
            },
          },
        },
      },
    },
    session: {
      initial: 'loggedOut',
      entry: 'storeFirstAuthAttemptDone',
      states: {
        loggedOut: {
          initial: 'idle',
          states: {
            idle: {},
            'customer-fetch-failed': {},
          },
          on: {
            LOGIN: {
              target: '#auth-machine.fetching.customer',
              actions: [
                {
                  type: 'persistCsrfToken',
                  params: ({ event }) => ({ csrfToken: event.csrfToken }),
                },
                'broadcastLogInEvent',
                {
                  type: 'storePostLoginRedirectHref',
                  params: ({ event }) => ({ postLoginRedirectHref: event.postLoginRedirectHref }),
                },
                'clearHasLoggedOut',
              ],
            },
          },
        },
        loggedIn: {
          on: {
            LOGOUT: {
              target: 'loggedOut',
              actions: [
                {
                  type: 'persistCsrfToken',
                  params: ({ event }) => ({ csrfToken: event.csrfToken }),
                },
                'broadcastLogOutEvent',
                'clearCurrentCustomerData',
                'storeHasLoggedOut',
              ],
            },
          },
        },
      },
    },
  },
  on: {
    UPDATE_CSRF_TOKEN: {
      actions: {
        type: 'persistCsrfToken',
        params: ({ event }) => ({ csrfToken: event.csrfToken }),
      },
    },
    CLEAR_POST_LOGIN_REDIRECT_HREF: {
      actions: 'clearPostLoginRedirectHref',
    },
  },
});
